import { useState } from "react";
import {
  BookOpen,
  Lightbulb,
  Layers,
} from "lucide-react";
import { PillarTile } from "../components/PillarTile";
import { RecentStrip } from "../components/RecentStrip";
import { ForYouRail } from "../components/ForYouRail";
import { ItemModal } from "../components/ItemModal";
import { useDashboardData } from "../hooks/useDashboardData";
import { useProfile } from "../hooks/useProfile";
import { useForYou } from "../hooks/useForYou";
import type { CatalogItem } from "../types/navigator";

export function DashboardPage() {
  const state = useDashboardData();
  const { profile } = useProfile();
  const forYou = useForYou(profile);
  const [selected, setSelected] = useState<CatalogItem | null>(null);

  const counts = state.counts;

  return (
    <div className="page dashboard-page">
      <div className="page-hero">
        <p className="page-subtitle">
          A single pane of glass for AI activity across Brookfield — Library,
          Initiatives, Platforms, and industry news.
        </p>
      </div>

      {state.errorCounts ? (
        <div className="error-banner" role="alert">
          <strong>Some metrics couldn't load.</strong> {state.errorCounts}
        </div>
      ) : null}

      {/* Band 1 — Pillar Tiles */}
      <section className="dash-section">
        <div className="dash-section-head">
          <h2 className="dash-section-title">Browse the catalog</h2>
          <p className="dash-section-sub">
            Jump into a pillar to explore the full list.
          </p>
        </div>
        <div className="pillar-grid">
          <PillarTile
            title="AI Library"
            description="Curated prompts, agents, tools, and templates approved for Brookfield use."
            count={counts?.library ?? null}
            countLabel="assets"
            icon={BookOpen}
            to="/library"
            accent="library"
            loading={state.loadingCounts}
          />
          <PillarTile
            title="AI Initiatives"
            description="Ideas and opportunities submitted by teams across regions."
            count={counts?.initiatives ?? null}
            countLabel="initiatives"
            icon={Lightbulb}
            to="/initiatives"
            accent="initiative"
            loading={state.loadingCounts}
          />
          <PillarTile
            title="AI Platforms"
            description="Approved platforms — sensitive-data policy, ownership, and capabilities."
            count={counts?.platforms ?? null}
            countLabel="platforms"
            icon={Layers}
            to="/platforms"
            accent="platform"
            loading={state.loadingCounts}
          />
        </div>
      </section>

      {/* Band 2 — For You (personalized library picks) */}
      <ForYouRail
        items={forYou.items}
        loading={forYou.loading}
        error={forYou.error}
        personalized={forYou.personalized}
        matchedOn={forYou.matchedOn}
        popularItems={forYou.popularItems}
        onOpen={setSelected}
      />

      {/* Band 3 — AI News (temporarily hidden) */}

      {/* Band 4 — Recently Added */}
      <RecentStrip
        items={state.recent}
        loading={state.loadingRecent}
        error={state.errorRecent}
        onOpen={setSelected}
      />

      {selected ? (
        <ItemModal
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            state.refetch();
            forYou.refetch();
          }}
        />
      ) : null}
    </div>
  );
}
